<script setup lang="ts">
    import Expense from '~/app/invoicing/models/Expense';
    import Contact from '~/app/contact/models/Contact';
    import ExpenseSchema from '~/app/invoicing/schemas/ExpenseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import { ModelType } from '~/app/base/schemas/BaseSchema';

    const currentExpense = ref<Expense | null>(null);
    const form = useForm<{
        contact: Contact | null;
    }>({
        contact: null,
    });

    const { modalName } = useModal(
        'invoicing:expense:add-contact-modal',
        (expense: Expense) => {
            currentExpense.value = expense;
            form.set('contact', expense.contact);
        },
        () => {
            currentExpense.value = null;
            form.reset();
        }
    );

    const isLoading = ref(false);

    const submit = async () => {
        if (isLoading.value) return;
        isLoading.value = true;
        if (!currentExpense.value) return;

        const endpoint = EndpointFactory.make(ModelType.EXPENSES);
        const schema = new ExpenseSchema({
            attributes: {},
        });
        schema.id = currentExpense.value.getId();

        if (form.get('contact')) {
            schema.addToOneRelationship('contact', ModelType.CONTACTS, form.get('contact')!.getId());
        }

        const response = await form.loadUntil(endpoint.update(schema));

        if (response.error) {
            if (!response.validationErrors) {
                useToasteoError();
            }
            return;
        }

        useToasteoSuccess('invoicing.expenses.updated_successfully');
        close();
        isLoading.value = false;
    };

    const close = () => {
        useEvent('modal:close', modalName);
    };
</script>

<template>
    <Modal
        :name="modalName"
        :width="500"
    >
        <div :class="$theme('modal.padding')">
            <div class="mt-4">
                <h2 :class="$theme('modal.titleLeft.text')">
                    {{ $t('invoicing.expense.add_contact_modal.title') }}
                </h2>
            </div>

            <form
                class="mt-2"
                @submit.prevent="submit"
            >
                <FormContactSelect
                    :form="form"
                    input-name="contact"
                    :label="$t('form.labels.supplier')"
                    contact-type="supplier"
                    without-label
                    with-create-button
                />
                <UiAction
                    :content="{
                        label: 'actions.save',
                        icon: 'fa-regular fa-check',
                        type: 'main',
                    }"
                    modifier="full"
                    :is-loading="isLoading"
                    @click="submit"
                />
            </form>
        </div>
    </Modal>
</template>
